import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FolderOpen, Trash2, Share2, Play, RefreshCw, Scale, CheckCircle2, AlertTriangle, DollarSign, BarChart3, Mail, Presentation, TrendingUp, Clock, ChevronDown, ChevronUp, Eye, Save, Target, Filter, Search, GitCompare, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShareScenarioDialog } from '../components/ShareScenarioDialog';
import EmailShareDialog from '../components/EmailShareDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import api, { getScenarios, getScenario, deleteScenario } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { formatCurrency } from '../lib/utils';
import { cn } from '../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Format ROI as multiplier
const formatROI = (value) => value != null ? `${(value / 100).toFixed(1)}x` : '0x';

// Calculate accuracy between projected and actual values
const calculateAccuracy = (projected, actual) => {
  if (actual === 0) return { diff: 0, status: 'neutral', label: 'N/A' };
  const diff = ((projected - actual) / Math.abs(actual)) * 100;
  const absDiff = Math.abs(diff);
  
  if (absDiff <= 10) {
    return { diff, status: 'accurate', label: 'Accurate' };
  } else if (diff > 0) {
    return { diff, status: 'over', label: `+${diff.toFixed(1)}% over` };
  } else {
    return { diff, status: 'under', label: `${diff.toFixed(1)}% under` };
  }
};

// Get accuracy badge styling
const getAccuracyBadgeStyle = (status) => {
  switch (status) {
    case 'accurate':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'over':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'under':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    default:
      return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

// Analysis Results Summary Card Component - matches calculator output style
const AnalysisResultsSummary = ({ scenario, currency, onViewDetails, onBoardReady }) => {
  const results = scenario.results || {};
  const config = scenario.configuration || {};
  
  const kpiUplift = results.kpi_delta_percent || results.total_kpi_delta_percent || 0;
  const financialImpact = results.financial_impact || results.total_financial_impact || 0;
  const netBenefit = results.net_benefit || results.total_net_benefit || 0;
  const roi = results.roi_percent || results.total_roi_percent || 0;
  const paybackMonths = results.payback_months || 
    (config.intervention_cost && financialImpact > 0 
      ? Math.ceil((config.intervention_cost || 100000) / (financialImpact / 12))
      : 0);
  
  return (
    <div className="space-y-4">
      {/* Header with scenario info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-teal-400" />
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Analysis Results</span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onViewDetails}
            className="gap-1"
            data-testid={`view-details-${scenario.id}`}
          >
            <Eye className="h-3.5 w-3.5" />
            View Details
          </Button>
          <Button 
            size="sm" 
            onClick={onBoardReady}
            className="gap-1 bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid={`board-ready-btn-${scenario.id}`}
          >
            <Presentation className="h-3.5 w-3.5" />
            Board Ready View
          </Button>
        </div>
      </div>
      
      {/* KPI tiles — dark-first (bg-[#111827]); the page's home-dark-scope /
          mc-light wrapper remaps them for dark & light, matching the health
          overview tiles (Maturity / Learning / Capability). */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid={`scenario-kpi-tiles-${scenario.id}`}>
        <ScenarioKpiTile icon={BarChart3} accent="teal" label="% Total KPI Uplift"
          value={`${kpiUplift.toFixed(2)}%`} sub="Modeled uplift across KPIs" />
        <ScenarioKpiTile icon={DollarSign} accent="sky" label="$ Financial Impact"
          value={formatCurrency(Math.round(financialImpact), currency)} sub="Annual value created" />
        <ScenarioKpiTile icon={TrendingUp} accent="emerald" label="Net Benefit"
          value={formatCurrency(Math.round(netBenefit), currency)} sub="After investment cost" />
        <ScenarioKpiTile icon={Target} accent="amber" label="ROI"
          value={formatROI(roi)} sub="Return multiple" />
        <ScenarioKpiTile icon={Clock} accent="cyan" label="Payback Period"
          value={paybackMonths > 0 ? `${paybackMonths.toFixed(1)} mo` : 'N/A'} sub="Time to break even" />
      </div>
    </div>
  );
};

// Shared KPI tile — mirrors the health overview KpiTiles (DarkCard look).
const TILE_ACCENT = {
  teal:    { icon: 'text-teal-400',    value: 'text-teal-300' },
  sky:     { icon: 'text-sky-400',     value: 'text-sky-300' },
  emerald: { icon: 'text-emerald-400', value: 'text-emerald-300' },
  amber:   { icon: 'text-amber-400',   value: 'text-amber-300' },
  cyan:    { icon: 'text-[#44b6d2]',   value: 'text-cyan-300' },
};
const ScenarioKpiTile = ({ icon: Icon, accent, label, value, sub }) => {
  const a = TILE_ACCENT[accent] || TILE_ACCENT.teal;
  return (
    <div className="rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl p-5 min-h-[128px] flex flex-col justify-between">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
        <Icon className={`h-3.5 w-3.5 ${a.icon}`} /> <span className="truncate">{label}</span>
      </div>
      <div>
        <p className={`text-2xl xl:text-3xl font-heading font-extrabold tabular-nums leading-tight ${a.value}`}>{value}</p>
        <p className="text-xs text-slate-400 mt-1">{sub}</p>
      </div>
    </div>
  );
};

export const ScenariosPage = () => {
  const navigate = useNavigate();
  const { id: scenarioIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const { loadScenario, currency } = useApp();
  const { viewAsOrg } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingScenarioId, setLoadingScenarioId] = useState(null);
  const [expandedScenarios, setExpandedScenarios] = useState({});
  const [expandedAccuracy, setExpandedAccuracy] = useState({});
  const [emailShareScenario, setEmailShareScenario] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [compareSel, setCompareSel] = useState([]);

  const toggleCompare = (id) => {
    setCompareSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return [...prev.slice(1), id]; // keep the latest four
      return [...prev, id];
    });
  };

  // Org filter from URL param > viewAsOrg > ''
  const initialOrg = searchParams.get('organization_id') || viewAsOrg?.id || '';
  const [selectedOrg, setSelectedOrg] = useState(initialOrg);
  const highlightId = searchParams.get('highlight') || scenarioIdParam || '';
  const [filterToHighlight, setFilterToHighlight] = useState(!!searchParams.get('highlight'));

  // Find original scenario for a projected scenario
  const findOriginalScenario = (projectedScenario) => {
    if (!projectedScenario.is_projected || !projectedScenario.original_scenario_id) return null;
    return scenarios.find(s => s.id === projectedScenario.original_scenario_id);
  };

  // Load organizations
  useEffect(() => {
    api.get('/people-engine/organizations').then(r => {
      setOrganizations(r.data?.organizations || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadScenarios();
  }, [selectedOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScenarios = async () => {
    setIsLoading(true);
    try {
      const response = await getScenarios(selectedOrg || undefined);
      setScenarios(response.data);
      // Auto-expand highlighted scenario, or first scenario
      if (highlightId) {
        setExpandedScenarios({ [highlightId]: true });
      } else if (response.data.length > 0) {
        setExpandedScenarios({ [response.data[0].id]: true });
      }
    } catch (error) {
      logger.error('Error loading scenarios:', error);
      toast.error('Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (scenarioId) => {
    try {
      await deleteScenario(scenarioId);
      setScenarios((prev) => prev.filter((s) => s.id !== scenarioId));
      toast.success('Scenario deleted');
    } catch (error) {
      logger.error('Delete error:', error);
      toast.error('Failed to delete scenario');
    }
  };

  const handleLoadScenario = useCallback(async (scenarioId, navigateToResults = false) => {
    setLoadingScenarioId(scenarioId);
    try {
      const response = await getScenario(scenarioId);
      const scenario = response.data;
      
      loadScenario(scenario);
      toast.success(`Loaded: ${scenario.name}`);
      
      if (navigateToResults) {
        navigate('/results');
      }
    } catch (error) {
      logger.error('Load error:', error);
      toast.error('Failed to load scenario');
    } finally {
      setLoadingScenarioId(null);
    }
  }, [navigate, loadScenario]);

  // Handle loading a shared scenario via URL parameter
  useEffect(() => {
    if (scenarioIdParam) {
      handleLoadScenario(scenarioIdParam, true);
    }
  }, [scenarioIdParam, handleLoadScenario]);


  const handleRerunScenario = async (scenarioId) => {
    try {
      const scenario = scenarios.find(s => s.id === scenarioId);
      if (!scenario) return;
      
      loadScenario(scenario, true);
      
      const analysisType = scenario.analysis_type || 'single';
      toast.success(`Loaded "${scenario.name}" for re-run. Update values, recalculate, and save.`, {
        duration: 5000,
      });
      
      if (analysisType === 'portfolio') {
        navigate('/portfolio');
      } else if (analysisType === 'matrix') {
        navigate('/matrix');
      } else {
        navigate('/configure');
      }
    } catch (error) {
      logger.error('Re-run error:', error);
      toast.error('Failed to load scenario for re-run');
    }
  };

  const toggleScenarioExpand = (scenarioId) => {
    setExpandedScenarios(prev => ({
      ...prev,
      [scenarioId]: !prev[scenarioId]
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const theme = getLightSectionTheme('Navy');

  const heroActions = (
    <>
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search scenarios…"
          className="w-[220px] pl-8 bg-white border-slate-200 text-slate-700"
          data-testid="scenarios-search"
        />
      </div>
      <Select value={selectedOrg || '_all'} onValueChange={v => setSelectedOrg(v === '_all' ? '' : v)}>
        <SelectTrigger className="w-[220px] bg-white border-slate-200 text-slate-700" data-testid="scenarios-org-filter">
          <Filter className="h-3.5 w-3.5 mr-1.5" />
          <SelectValue placeholder="All Organizations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Organizations</SelectItem>
          {organizations.map(o => (
            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        onClick={() => navigate('/configure')}
        className={theme.actionBtn}
        data-testid="new-analysis-btn"
      >
        New Analysis
      </Button>
    </>
  );

  const heroStats = [
    { label: 'Total Scenarios', value: scenarios.length },
    { label: 'Projected', value: scenarios.filter(s => s.is_projected).length },
    { label: 'With Actuals', value: scenarios.filter(s => s.actual_results || (s.is_projected && s.original_scenario_id)).length },
  ];

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Saved Scenarios"
          description="View and manage your saved analysis scenarios. Compare results, track accuracy against actuals, and share with stakeholders."
          theme={theme}
          icon={FolderOpen}
          actions={heroActions}
          stats={heroStats}
          testId="scenarios-hero"
        />
      )}
    >
      <div className={cn('space-y-6 animate-fade-in', isDark ? 'home-dark-scope' : 'mc-light')} data-testid="scenarios-page">
        {scenarios.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16">
            <div className="text-center">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No Saved Scenarios</h3>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                Run an analysis and save it to see results here.
                Saved scenarios can be viewed, shared, and compared.
              </p>
              <Button
                onClick={() => navigate('/configure')}
                className="mt-6 bg-navy-700 hover:bg-navy-800"
                data-testid="create-first-scenario-btn"
              >
                Create First Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Compare selection bar */}
          {compareSel.length > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5" data-testid="scenarios-compare-bar">
              <p className="text-sm text-indigo-800 font-medium">
                {compareSel.length}/4 selected to compare{compareSel.length === 1 ? ' — pick at least one more' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setCompareSel([])} data-testid="scenarios-compare-clear">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-500 text-white" disabled={compareSel.length < 2}
                  onClick={() => navigate(`/compare?ids=${compareSel.join(',')}`)} data-testid="scenarios-compare-go">
                  <GitCompare className="h-3.5 w-3.5 mr-1" /> Compare selected
                </Button>
              </div>
            </div>
          )}
          {/* Filter banner when viewing a specific business case */}
          {filterToHighlight && highlightId && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
              <p className="text-sm text-amber-800 font-medium">Showing selected business case from Financial Health</p>
              <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-100" onClick={() => setFilterToHighlight(false)} data-testid="show-all-scenarios-btn">
                Show All ({scenarios.length})
              </Button>
            </div>
          )}
          {(() => {
            const base = filterToHighlight && highlightId ? scenarios.filter(s => s.id === highlightId) : scenarios;
            const q = searchQuery.trim().toLowerCase();
            const visible = q ? base.filter(s => (s.name || '').toLowerCase().includes(q)) : base;
            if (visible.length === 0) {
              return <p className="text-sm text-slate-500 py-8 text-center" data-testid="scenarios-search-empty">No scenarios match "{searchQuery}".</p>;
            }
            return visible.map((scenario) => {
            const isCurrentlyLoading = loadingScenarioId === scenario.id || loadingScenarioId === scenario.share_token;
            const isExpanded = expandedScenarios[scenario.id];
            
            return (
              <Card 
                key={scenario.id} 
                className={`overflow-hidden transition-all duration-200 ${
                  highlightId === scenario.id ? 'border-amber-400 ring-2 ring-amber-200 shadow-md' :
                  scenario.is_projected ? 'border-amber-200 bg-amber-50/30' : 'hover:shadow-md'
                }`}
                data-testid={`scenario-card-${scenario.id}`}
              >
                {/* Scenario Header - Always Visible */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                  onClick={() => toggleScenarioExpand(scenario.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <label className="flex items-center pl-1" onClick={(e) => e.stopPropagation()} title="Select to compare">
                        <input
                          type="checkbox"
                          checked={compareSel.includes(scenario.id)}
                          onChange={() => toggleCompare(scenario.id)}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                          data-testid={`compare-check-${scenario.id}`}
                        />
                      </label>
                      <button 
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                        data-testid={`expand-scenario-${scenario.id}`}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-500" />
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {scenario.name}
                          </h3>
                          {scenario.is_projected && (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              Projected
                            </Badge>
                          )}
                          {scenario.analysis_type && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
                              {scenario.analysis_type}
                            </span>
                          )}
                          {scenario.constraint_context?.label && (
                            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px]" data-testid="scenario-constraint-badge">
                              Recovers {scenario.constraint_context.label} leak
                            </Badge>
                          )}
                          {scenario.version > 1 && (
                            <Badge variant="outline" className="text-[10px]">
                              v{scenario.version}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3.5 w-3.5" />
                            {scenario.configuration?.lei_improvement}% LEI
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {new Date(scenario.created_at).toLocaleDateString()}
                          </span>
                          {scenario.description && (
                            <span className="text-slate-400 truncate max-w-xs">
                              {scenario.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions - visible in header */}
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleLoadScenario(scenario.id, true)}
                        disabled={isCurrentlyLoading}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid={`load-scenario-${scenario.id}`}
                      >
                        {isCurrentlyLoading ? (
                          <div className="spinner h-4 w-4" />
                        ) : (
                          <>
                            <Play className="mr-1 h-3 w-3" />
                            Load
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRerunScenario(scenario.id)}
                        title="Re-run with updated data"
                        data-testid={`rerun-scenario-${scenario.id}`}
                      >
                        <RefreshCw className="mr-1 h-3 w-3" />
                        Re-run
                      </Button>
                      <ShareScenarioDialog 
                        scenario={scenario}
                        trigger={
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Share"
                            data-testid={`share-scenario-${scenario.id}`}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEmailShareScenario(scenario)}
                        title="Share via email"
                        data-testid={`email-share-scenario-${scenario.id}`}
                      >
                        <Mail className="h-4 w-4 text-teal-600" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-rose-500 hover:text-rose-700"
                            data-testid={`delete-scenario-${scenario.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Scenario</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{scenario.name}"? 
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(scenario.id)}
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Analysis Results Summary */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50/50 p-4">
                    <AnalysisResultsSummary 
                      scenario={scenario} 
                      currency={currency}
                      onViewDetails={() => handleLoadScenario(scenario.id, true)}
                      onBoardReady={() => navigate(`/board-ready/${scenario.id}`)}
                    />

                    {/* Projection vs Actual Comparison for projected scenarios */}
                    {scenario.is_projected && (() => {
                      const original = findOriginalScenario(scenario);
                      if (!original) return null;
                      
                      const isAccuracyExpanded = expandedAccuracy[scenario.id];
                      
                      const projectedRoi = scenario.results?.roi_percent || 0;
                      const actualRoi = original.results?.roi_percent || original.results?.total_roi_percent || 0;
                      const roiAccuracy = calculateAccuracy(projectedRoi, actualRoi);
                      
                      const projectedFinancial = scenario.results?.financial_impact || 0;
                      const actualFinancial = original.results?.financial_impact || original.results?.total_financial_impact || 0;
                      const financialAccuracy = calculateAccuracy(projectedFinancial, actualFinancial);
                      
                      const projectedKpi = scenario.results?.kpi_delta_percent || 0;
                      const actualKpi = original.results?.kpi_delta_percent || original.results?.total_kpi_delta_percent || 0;
                      const kpiAccuracy = calculateAccuracy(projectedKpi, actualKpi);
                      
                      const avgAccuracy = (Math.abs(roiAccuracy.diff) + Math.abs(financialAccuracy.diff) + Math.abs(kpiAccuracy.diff)) / 3;
                      const overallStatus = avgAccuracy <= 10 ? 'accurate' : avgAccuracy <= 20 ? 'moderate' : 'poor';

                      return (
                        <div className="mt-4 pt-4 border-t border-amber-200">
                          <button
                            onClick={() => setExpandedAccuracy(prev => ({ ...prev, [scenario.id]: !prev[scenario.id] }))}
                            className="w-full flex items-center justify-between text-left"
                            data-testid={`toggle-accuracy-${scenario.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Scale className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-700">Projection vs Actual</span>
                              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                overallStatus === 'accurate' ? 'bg-emerald-100 text-emerald-700' :
                                overallStatus === 'moderate' ? 'bg-amber-100 text-amber-700' :
                                'bg-rose-100 text-rose-700'
                              }`}>
                                {overallStatus === 'accurate' ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                {overallStatus === 'accurate' ? 'Accurate' : 
                                 overallStatus === 'moderate' ? 'Moderate' : 'Needs Calibration'}
                              </div>
                            </div>
                            <span className="text-xs text-amber-600">{isAccuracyExpanded ? '▲ Hide' : '▼ Show details'}</span>
                          </button>
                          
                          {isAccuracyExpanded && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* ROI */}
                            <div className="p-3 rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1">
                                  <TrendingUp className="h-3 w-3 text-teal-400" />
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">ROI</span>
                                </div>
                                <Badge variant="outline" className={`text-[9px] ${getAccuracyBadgeStyle(roiAccuracy.status)}`}>
                                    {roiAccuracy.label}
                                  </Badge>
                                </div>
                                <div className="text-xs space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Projected:</span>
                                    <span className="text-amber-300 font-semibold">{(projectedRoi / 100).toFixed(2)}x</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Actual:</span>
                                    <span className="text-slate-200 font-semibold">{(actualRoi / 100).toFixed(2)}x</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Financial Impact */}
                            <div className="p-3 rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1">
                                  <DollarSign className="h-3 w-3 text-emerald-400" />
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Financial</span>
                                </div>
                                <Badge variant="outline" className={`text-[9px] ${getAccuracyBadgeStyle(financialAccuracy.status)}`}>
                                    {financialAccuracy.label}
                                  </Badge>
                                </div>
                                <div className="text-xs space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Projected:</span>
                                    <span className="text-amber-300 font-semibold">{formatCurrency(Math.round(projectedFinancial), currency)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Actual:</span>
                                    <span className="text-slate-200 font-semibold">{formatCurrency(Math.round(actualFinancial), currency)}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* KPI Uplift */}
                            <div className="p-3 rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1">
                                  <BarChart3 className="h-3 w-3 text-sky-400" />
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">KPI Uplift</span>
                                </div>
                                <Badge variant="outline" className={`text-[9px] ${getAccuracyBadgeStyle(kpiAccuracy.status)}`}>
                                    {kpiAccuracy.label}
                                  </Badge>
                                </div>
                                <div className="text-xs space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Projected:</span>
                                    <span className="text-amber-300 font-semibold">{projectedKpi.toFixed(2)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Actual:</span>
                                    <span className="text-slate-200 font-semibold">{actualKpi.toFixed(2)}%</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <p className="text-[10px] text-slate-500 mt-2">
                            Compared against: {original.name}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </Card>
            );
          });
          })()}
        </div>
      )}
      
      {/* Email Share Dialog */}
      <EmailShareDialog
        open={!!emailShareScenario}
        onOpenChange={(open) => !open && setEmailShareScenario(null)}
        scenario={emailShareScenario}
      />
      </div>
    </PageShell>
  );
};

export default ScenariosPage;
